import React, { useState } from "react";
import { Button, Checkbox, Input, Modal, Space, Tag } from "antd";
import { CheckCircleFilled, EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { CountryDropdown } from "react-country-region-selector";
import { getCountries, getCountryCallingCode } from "react-phone-number-input/input";
import { useNavigate } from "react-router-dom";
import "./registration.css";
import "../auth-common.css";
import { ErrorToast, SuccessToast } from "../../widgets/AppToast";
import { post } from "../../services/services";
import { ApiURL } from "../../services/apiConstants";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordPattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

const Registration = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", company_name: "", company_email: "", password: "", confirm_password: "",
    company_phone: "", alternate_email: "", country: "", city: "", state: "",
    zip_code: "", address: "", country_code: "65", terms_conditions: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [registrationVerificationToken, setRegistrationVerificationToken] = useState(null);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: "" }));

    if (field === "company_email" && (emailVerified || registrationVerificationToken)) {
      setEmailVerified(false);
      setRegistrationVerificationToken(null);
    }
  };

  const responsePayload = (response) => response?.response?.data || response;

  const responseError = (response, fallback) => {
    const payload = responsePayload(response);
    const apiErrors = payload?.errors || {};
    if (Object.keys(apiErrors).length) setErrors((current) => ({ ...current, ...apiErrors }));
    return payload?.message || Object.values(apiErrors)[0] || fallback;
  };

  const companyEmailError = (response, fallback) =>
    responsePayload(response)?.errors?.company_email || responsePayload(response)?.message || fallback;

  const validateEmailForOtp = () => {
    if (!form.company_email.trim()) {
      setErrors((current) => ({ ...current, company_email: "Company email is required." }));
      return false;
    }
    if (!emailPattern.test(form.company_email.trim())) {
      setErrors((current) => ({ ...current, company_email: "Enter a valid company email." }));
      return false;
    }
    return true;
  };

  const sendRegistrationOtp = async () => {
    if (!validateEmailForOtp()) return;
    setSendingOtp(true);
    try {
      const data = new FormData();
      data.append("company_email", form.company_email.trim());
      const response = await post(ApiURL.send_registration_otp, data);
      const payload = responsePayload(response);
      if (payload?.status === true && payload?.email_available === true) {
        setOtp("");
        setErrors((current) => ({ ...current, company_email: "" }));
        setOtpModalOpen(true);
        SuccessToast("OTP Sent", payload.message || "A verification code has been sent to your company email.");
      } else if (payload?.email_available === false) {
        const error = companyEmailError(payload, "This company email is unavailable for registration.");
        setEmailVerified(false);
        setRegistrationVerificationToken(null);
        setOtp("");
        setOtpModalOpen(false);
        setErrors((current) => ({ ...current, company_email: error }));
      } else {
        ErrorToast("Unable to Send OTP", responseError(response, "Please try again."));
      }
    } catch (_) {
      ErrorToast("Unable to Send OTP", "Please try again.");
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyRegistrationOtp = async () => {
    if (otp.length !== 4) {
      setErrors((current) => ({ ...current, otp: "Enter the 4-digit verification code." }));
      return;
    }
    setVerifyingOtp(true);
    try {
      const data = new FormData();
      data.append("company_email", form.company_email.trim());
      data.append("otp", otp);
      const response = await post(ApiURL.registration_otp_verification, data);
      if (response?.status && response?.registration_verification_token) {
        setEmailVerified(true);
        setRegistrationVerificationToken(response.registration_verification_token);
        setOtpModalOpen(false);
        setOtp("");
        setErrors((current) => ({ ...current, company_email: "", otp: "" }));
        SuccessToast("Email Verified", "Your company email has been verified successfully.");
      } else {
        setErrors((current) => ({ ...current, otp: responseError(response, "Invalid OTP. Please try again.") }));
      }
    } catch (_) {
      setErrors((current) => ({ ...current, otp: "Unable to verify OTP. Please try again." }));
    } finally {
      setVerifyingOtp(false);
    }
  };

  const resendRegistrationOtp = async () => {
    setVerifyingOtp(true);
    try {
      const data = new FormData();
      data.append("company_email", form.company_email.trim());
      data.append("otp_type", "registration");
      const response = await post(ApiURL.resent_otp, data);
      if (response?.status) {
        setOtp("");
        setEmailVerified(false);
        setRegistrationVerificationToken(null);
        setErrors((current) => ({ ...current, otp: "" }));
        SuccessToast("OTP Resent", response.message || "A new verification code has been sent.");
      } else {
        setErrors((current) => ({ ...current, otp: responseError(response, "Unable to resend OTP.") }));
      }
    } catch (_) {
      setErrors((current) => ({ ...current, otp: "Unable to resend OTP. Please try again." }));
    } finally {
      setVerifyingOtp(false);
    }
  };

  const validateRegistration = () => {
    const nextErrors = {};
    const required = ["name", "company_name", "company_email", "company_phone", "country", "city", "state", "zip_code", "address"];
    required.forEach((field) => { if (!String(form[field]).trim()) nextErrors[field] = "This field is required."; });
    if (form.name && /^\d+$/.test(form.name.trim())) nextErrors.name = "Full name cannot be entirely numeric.";
    if (form.company_name && /^\d+$/.test(form.company_name.trim())) nextErrors.company_name = "Company name cannot be entirely numeric.";
    if (form.company_email && !emailPattern.test(form.company_email.trim())) nextErrors.company_email = "Enter a valid company email.";
    if (form.alternate_email && !emailPattern.test(form.alternate_email.trim())) nextErrors.alternate_email = "Enter a valid alternate email.";
    if (form.alternate_email && form.alternate_email.trim().toLowerCase() === form.company_email.trim().toLowerCase()) nextErrors.alternate_email = "Alternate email must differ from company email.";
    if (form.company_phone && !/^\d{8,10}$/.test(form.company_phone)) nextErrors.company_phone = "Enter an 8–10 digit phone number.";
    if (form.zip_code && !/^\d{5,10}$/.test(form.zip_code)) nextErrors.zip_code = "Enter a 5–10 digit ZIP / postal code.";
    if (!passwordPattern.test(form.password)) nextErrors.password = "Use 8+ characters with a letter, number, and special character.";
    if (form.password !== form.confirm_password) nextErrors.confirm_password = "Passwords do not match.";
    if (!form.terms_conditions) nextErrors.terms_conditions = "Please accept the terms and conditions.";
    if (!emailVerified || !registrationVerificationToken) nextErrors.company_email = "Please verify your company email before registration.";
    setErrors(nextErrors);
    return !Object.keys(nextErrors).length;
  };

  const submitRegistration = async () => {
    if (!validateRegistration()) return;
    setLoading(true);
    try {
      const data = new FormData();
      ["name", "company_name", "company_email", "password", "confirm_password", "company_phone", "alternate_email", "country", "city", "state", "zip_code", "address", "country_code"].forEach((field) => data.append(field, form[field].trim()));
      data.append("role", "company");
      data.append("terms_conditions", form.terms_conditions ? "true" : "false");
      data.append("registration_verification_token", registrationVerificationToken);
      const response = await post(ApiURL.sinup, data);
      const payload = responsePayload(response);
      if (payload?.status) {
        SuccessToast("Company Registration Successful", "Your company account has been created. Please log in using your company email and password.");
        navigate("/");
      } else {
        const error = companyEmailError(payload, "Please review the form and try again.");
        setErrors((current) => ({ ...current, ...payload?.errors, company_email: error }));
        if (payload?.errors?.company_email || payload?.message) {
          setEmailVerified(false);
          setRegistrationVerificationToken(null);
          setOtp("");
        }
        ErrorToast("Registration Failed", error);
      }
    } catch (_) {
      ErrorToast("Registration Failed", "There was an error during registration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldError = (field) => errors[field] && <span className="registration-error">{errors[field]}</span>;

  return (
    <div className="auth-page-container registration-page">
      <div className="auth-glass-card auth-registration-card">
        <div className="auth-header"><h5>MarinBytes — Data, Ships, People and Sustainability</h5><h2>New Company Registration</h2></div>
        <p className="registration-intro">Join MarinBytes and streamline compliance, operations and sustainability across your fleet.</p>
        <div className="registration-form">
          <div className="registration-row registration-row--two">
            <div className="auth-input-group"><label>Full Name <span className="required">*</span></label><Input size="large" value={form.name} placeholder="Enter your full name" onChange={(e) => updateField("name", e.target.value)} />{fieldError("name")}</div>
            <div className="auth-input-group"><label>Company Name <span className="required">*</span></label><Input size="large" value={form.company_name} placeholder="Enter company name" onChange={(e) => updateField("company_name", e.target.value)} />{fieldError("company_name")}</div>
          </div>
          <div className="registration-row registration-row--three">
            <div className="auth-input-group registration-email-field"><label>Company Email <span className="required">*</span></label><Space.Compact><Input size="large" value={form.company_email} disabled={emailVerified} placeholder="company@example.com" onChange={(e) => updateField("company_email", e.target.value)} /><Button size="large" type={emailVerified ? "default" : "primary"} disabled={emailVerified} loading={sendingOtp} onClick={sendRegistrationOtp}>{emailVerified ? <><CheckCircleFilled /> Verified</> : "Verify Email"}</Button></Space.Compact>{fieldError("company_email")}</div>
            <div className="auth-input-group"><label>Password <span className="required">*</span></label><Input.Password size="large" value={form.password} placeholder="Create password" iconRender={(visible) => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />} onChange={(e) => updateField("password", e.target.value)} />{fieldError("password")}</div>
            <div className="auth-input-group"><label>Confirm Password <span className="required">*</span></label><Input.Password size="large" value={form.confirm_password} placeholder="Confirm password" iconRender={(visible) => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />} onChange={(e) => updateField("confirm_password", e.target.value)} />{fieldError("confirm_password")}</div>
          </div>
          <div className="registration-row registration-row--three">
            <div className="auth-input-group"><label>Alternate Email (Optional)</label><Input size="large" value={form.alternate_email} placeholder="alternate@example.com" onChange={(e) => updateField("alternate_email", e.target.value)} />{fieldError("alternate_email")}</div>
            <div className="auth-input-group"><label>Phone Number <span className="required">*</span></label><Space.Compact><select className="country-code-select" value={form.country_code} onChange={(e) => updateField("country_code", e.target.value)}>{getCountries().map((code) => <option key={code} value={getCountryCallingCode(code)}>+{getCountryCallingCode(code)}</option>)}</select><Input size="large" maxLength={10} value={form.company_phone} placeholder="Enter phone number" onChange={(e) => updateField("company_phone", e.target.value.replace(/\D/g, ""))} /></Space.Compact>{fieldError("company_phone")}</div>
            <div className="auth-input-group"><label>Country <span className="required">*</span></label><CountryDropdown value={form.country} onChange={(value) => updateField("country", value)} className="registration-country-dropdown" />{fieldError("country")}</div>
          </div>
          <div className="registration-row registration-row--four">
            <div className="auth-input-group"><label>City <span className="required">*</span></label><Input size="large" value={form.city} placeholder="Enter city" onChange={(e) => updateField("city", e.target.value)} />{fieldError("city")}</div>
            <div className="auth-input-group"><label>State / Region <span className="required">*</span></label><Input size="large" value={form.state} placeholder="Enter state / region" onChange={(e) => updateField("state", e.target.value)} />{fieldError("state")}</div>
            <div className="auth-input-group"><label>ZIP / Postal Code <span className="required">*</span></label><Input size="large" maxLength={10} value={form.zip_code} placeholder="Enter ZIP / postal code" onChange={(e) => updateField("zip_code", e.target.value.replace(/\D/g, ""))} />{fieldError("zip_code")}</div>
            <div className="auth-input-group registration-address"><label>Office Address <span className="required">*</span></label><Input size="large" value={form.address} placeholder="Enter complete office address" onChange={(e) => updateField("address", e.target.value)} />{fieldError("address")}</div>
          </div>
        </div>
        <div className="registration-terms"><Checkbox checked={form.terms_conditions} onChange={(e) => updateField("terms_conditions", e.target.checked)}>I agree to the <button type="button" className="auth-link-btn terms-link" onClick={() => setTermsModalOpen(true)}>Terms of Service</button> and <button type="button" className="auth-link-btn terms-link" onClick={() => setPrivacyModalOpen(true)}>Privacy Policy</button>.</Checkbox>{fieldError("terms_conditions")}</div>
        <Button className="auth-primary-btn registration-submit" type="primary" loading={loading} onClick={submitRegistration}>Submit Registration</Button>
        <div className="registration-login">Already have an account? <button type="button" className="auth-link-btn terms-link" onClick={() => navigate("/")}>Log in here</button></div>
      </div>

      <Modal title="Verify Company Email" open={otpModalOpen} onCancel={() => setOtpModalOpen(false)} footer={[<Button key="cancel" onClick={() => setOtpModalOpen(false)}>Cancel</Button>, <Button key="verify" type="primary" loading={verifyingOtp} onClick={verifyRegistrationOtp}>Verify Email</Button>]}>
        <p>We have sent a 4-digit verification code to <Tag color="blue">{form.company_email}</Tag></p>
        <p> Please enter the code below to complete your registration.</p>
        <br></br>
        <Input.OTP length={4} value={otp} onChange={(value) => { setOtp(value); setErrors((current) => ({ ...current, otp: "" })); }} />
        {fieldError("otp")}
        <div className="otp-resend">Didn't receive the code? <button type="button" className="auth-link-btn terms-link" disabled={verifyingOtp} onClick={resendRegistrationOtp}>Resend OTP</button></div>
      </Modal>
      <Modal
        className="terms-conditions-modal"
        title="Terms of Service"
        open={termsModalOpen}
        onOk={() => setTermsModalOpen(false)}
        onCancel={() => setTermsModalOpen(false)}
        width={800}
        okText="I Agree"
        bodyStyle={{
          maxHeight: "60vh",
          overflowY: "auto",
          paddingRight: "16px",
        }}
      >
        <div className="terms-content">

          <h3>1. Acceptance of Terms</h3>
          <p>
            By accessing or using the MarineBytes platform ("Services"), you agree
            to be bound by these Terms of Service. If you do not agree with
            these Terms, you must not access or use the Services.
          </p>

          <h3>2. Permitted Use</h3>
          <p>
            The Services are intended solely for use by maritime organizations,
            ship owners, ship managers, charterers, manning agencies, and related
            maritime service providers for lawful business purposes.
          </p>

          <h3>3. User Responsibilities</h3>
          <p>
            Users are responsible for maintaining the confidentiality of their
            account credentials and for all activities performed under their
            accounts.
          </p>

          <p>Users agree not to:</p>

          <ul>
            <li>
              Use the Services for any unlawful or unauthorized purpose.
            </li>
            <li>
              Upload, transmit, or distribute malicious software, viruses, or
              harmful content.
            </li>
            <li>
              Attempt to gain unauthorized access to the platform, systems, or
              data.
            </li>
            <li>
              Interfere with or disrupt the integrity, security, or performance of
              the Services.
            </li>
            <li>
              Provide false, inaccurate, or misleading information.
            </li>
          </ul>

          <h3>4. Intellectual Property</h3>
          <p>
            All software, source code, trademarks, logos, designs, documentation,
            and other intellectual property associated with MarineBytes remain the
            exclusive property of MarineByte Ltd. and are protected under
            applicable intellectual property laws.
          </p>

          <p>
            Unauthorized reproduction, modification, distribution, or use of any
            intellectual property is strictly prohibited.
          </p>

          <h3>5. Data Ownership</h3>
          <p>
            All operational, vessel, compliance, and business data entered into the
            platform by users shall remain the property of the respective customer.
          </p>

          <p>
            By using the Services, users grant MarineByte Ltd. a limited right to
            process, store, and use such data solely for the purpose of providing,
            maintaining, and improving the Services.
          </p>

          <h3>6. Privacy and Data Protection</h3>
          <p>
            MarineByte Ltd. is committed to protecting user privacy and processing
            personal and business data in accordance with applicable data protection
            and privacy laws.
          </p>

          <p>
            Users are encouraged to review the Privacy Policy to understand how
            information is collected, processed, stored, and protected.
          </p>

          <h3>7. Service Availability</h3>
          <p>
            MarineByte Ltd. strives to provide reliable and uninterrupted access to
            the Services. However, uninterrupted availability cannot be guaranteed.
          </p>

          <p>
            The Services may be temporarily unavailable due to scheduled
            maintenance, system upgrades, technical issues, force majeure events,
            or interruptions caused by third-party service providers.
          </p>

          <h3>8. Limitation of Liability</h3>
          <p>
            To the fullest extent permitted by applicable law, MarineByte Ltd. shall
            not be liable for any indirect, incidental, consequential, special,
            punitive, or exemplary damages arising out of or related to the use of,
            or inability to use, the Services.
          </p>

          <p>
            MarineByte Ltd.'s total liability arising under these Terms shall not
            exceed the amount paid by the user, if any, for accessing the Services
            during the preceding twelve (12) months.
          </p>

          <h3>9. Disclaimer</h3>
          <p>
            The Services are provided on an "as is" and "as available" basis,
            without warranties of any kind, whether express or implied.
          </p>

          <p>MarineByte Ltd. does not warrant that:</p>

          <ul>
            <li>
              The Services will be uninterrupted, error-free, or completely secure.
            </li>
            <li>
              The information or data available through the Services will always be
              accurate, complete, or current.
            </li>
            <li>
              Any defects or errors will be corrected immediately.
            </li>
          </ul>

          <p>
            Users acknowledge that they use the Services at their own risk and are
            responsible for independently verifying critical operational and
            compliance information.
          </p>

          <h3>10. Termination</h3>
          <p>
            MarineByte Ltd. reserves the right to suspend, restrict, or terminate
            access to the Services, with or without notice, if a user violates
            these Terms, engages in unauthorized activities, or misuses the
            platform.
          </p>

          <p>
            Users may discontinue use of the Services at any time, subject to any
            applicable contractual obligations.
          </p>

          <h3>11. Governing Law</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the
            laws of India, without regard to conflict of law principles.
          </p>

          <p>
            Any disputes arising out of or in connection with these Terms shall be
            subject to the exclusive jurisdiction of the competent courts located
            in India.
          </p>

          <h3>12. Support / Contact Information</h3>
          <p>
            For support, inquiries, or questions regarding these Terms, users may
            contact MarineByte Ltd. through the following channel:
          </p>

          <p>
            <strong>Email:</strong> enquiry@marinbytes.com
          </p>

          <p>
            MarineByte Ltd. will make reasonable efforts to respond to support
            requests within 48 business hours.
          </p>

          <h3>13. Updates to Terms</h3>
          <p>
            MarineByte Ltd. reserves the right to modify, amend, or update these
            Terms at any time.
          </p>

          <p>
            Any changes will become effective upon publication on the platform.
            Continued use of the Services after such changes constitutes acceptance
            of the revised Terms.
          </p>
          <p style={{ marginTop: "24px", fontSize: "12px", color: "#666" }}>
    <strong>Last Updated:</strong> 26 August 2026
  </p>


        </div>
      </Modal>
      <Modal
        className="privacy-policy-modal"
        title="Privacy Policy"
        open={privacyModalOpen}
        onOk={() => setPrivacyModalOpen(false)}
        onCancel={() => setPrivacyModalOpen(false)}
        width={800}
        okText="I Agree"
        bodyStyle={{
          maxHeight: "60vh",
          overflowY: "auto",
          paddingRight: "16px",
        }}
      >
<div className="terms-content">

  <h3>1. Introduction</h3>
  <p>
    MarinBytes Pvt. Ltd. ("MarinBytes", "we", "us", or "our") respects the
    privacy of individuals who use our website, web applications, software
    applications, maritime technology platforms, and related services
    (collectively, the "Services"). This Privacy Policy explains how we
    collect, use, disclose, retain, protect, and otherwise process personal
    information in connection with the Services.
  </p>
  <p>
    By accessing or using the Services, you acknowledge that you have read
    and understood this Privacy Policy. Where applicable law requires consent
    or another lawful basis, MarinBytes will obtain or rely on that basis as
    required by law.
  </p>

  <h3>2. Scope</h3>
  <p>
    This Privacy Policy applies to personal information collected through or
    in connection with:
  </p>
  <ul>
    <li>The MarinBytes website and web applications.</li>
    <li>
      MarinBytes mobile, desktop, or other software applications, where
      applicable.
    </li>
    <li>
      Company registration, account creation, user onboarding, and account
      administration.
    </li>
    <li>
      Customer support, service communications, product demonstrations,
      trials, and pilot implementations.
    </li>
    <li>
      Information provided to MarinBytes by customers, company administrators,
      or authorised representatives.
    </li>
    <li>Other business interactions relating to the Services.</li>
  </ul>
  <p>
    This Policy does not replace any separate agreement, contract, DPA, or
    other written arrangement between MarinBytes and a customer. If there is
    a conflict regarding customer-controlled data, the applicable customer
    agreement or DPA will govern to the extent permitted by law.
  </p>

  <h3>3. Information We Collect</h3>

  <h4>3.1 Account and Business Information</h4>
  <ul>
    <li>Name and business contact details.</li>
    <li>Business email address and phone number.</li>
    <li>Job title, designation, department, or role.</li>
    <li>Company name, address, and business contact details.</li>
    <li>
      Company registration, tax, billing, or related information where
      required.
    </li>
    <li>Account credentials, authentication information, user roles, and permissions.</li>
  </ul>

  <h4>3.2 Vessel and Operational Information</h4>
  <p>
    Where customers use MarinBytes maritime modules, the Services may process:
  </p>
  <ul>
    <li>Vessel details, IMO numbers, and other vessel identifiers.</li>
    <li>Voyage and operational information.</li>
    <li>Crew and staff information entered by authorised users.</li>
    <li>
      Digital logbook records, Oil Record Book records, and other operational
      records.
    </li>
    <li>Work/rest records, maintenance, and machinery information.</li>
    <li>
      Fuel, consumption, emissions, CII, compliance, and reporting information.
    </li>
    <li>
      Other operational, technical, or business information entered by or on
      behalf of the customer.
    </li>
  </ul>
  <p>
    Some vessel, crew, and operational information may constitute customer or
    business data rather than information controlled by MarinBytes for its own
    purposes. Where MarinBytes processes such information on behalf of a
    customer, MarinBytes will process it in accordance with the customer's
    instructions and the applicable agreement or DPA, subject to applicable law.
  </p>

  <h4>3.3 Technical and Usage Information</h4>
  <ul>
    <li>IP address and approximate network information.</li>
    <li>Browser type and version, device type, and operating system.</li>
    <li>Login, authentication, session, and access timestamps.</li>
    <li>Application activity, usage, performance, and diagnostic information.</li>
    <li>Error reports, security logs, audit logs, and system monitoring information.</li>
  </ul>

  <h4>3.4 Communications</h4>
  <ul>
    <li>Support requests and correspondence.</li>
    <li>Feedback, demo requests, product enquiries, and survey responses.</li>
    <li>Information included in communications with MarinBytes.</li>
  </ul>

  <h3>4. How We Collect Information</h3>
  <ul>
    <li>Directly from users during registration, onboarding, or account management.</li>
    <li>From customers, company administrators, or authorised representatives.</li>
    <li>When users enter information into the Services.</li>
    <li>Automatically through technical, security, and usage logs.</li>
    <li>Through email, telephone, support, and other communications.</li>
    <li>
      From third-party authentication or identity providers when an available
      login option is used.
    </li>
  </ul>
  <p>
    Where a customer or administrator provides personal information about
    another individual, the customer or administrator is responsible for
    ensuring that the information is collected and provided lawfully and that
    any required notices or permissions have been addressed.
  </p>

  <h3>5. Purposes of Processing</h3>
  <p>MarinBytes may process personal information for purposes including:</p>
  <ul>
    <li>Creating, administering, and securing user accounts.</li>
    <li>Providing, operating, maintaining, and supporting the Services.</li>
    <li>Authenticating users and managing roles and permissions.</li>
    <li>Managing company, vessel, user, and system configurations.</li>
    <li>Providing customer support and responding to enquiries.</li>
    <li>Maintaining and improving functionality, reliability, and user experience.</li>
    <li>Diagnosing technical problems and preventing misuse.</li>
    <li>
      Maintaining security, audit trails, system integrity, backups, and
      business continuity.
    </li>
    <li>
      Communicating important service, security, account, and contractual
      information.
    </li>
    <li>Processing demonstrations, trials, pilots, and onboarding requests.</li>
    <li>
      Complying with applicable legal, regulatory, contractual, and maritime
      requirements.
    </li>
    <li>
      Detecting, preventing, investigating, and addressing fraud, abuse,
      security incidents, and other unlawful activity.
    </li>
  </ul>
  <p>
    MarinBytes aims to apply data protection principles including purpose
    limitation, data minimisation, accuracy, storage limitation, and
    confidentiality, as applicable to the processing and the law governing it.
  </p>

  <h3>6. Lawful Basis</h3>
  <p>
    Depending on the applicable law and the nature of the processing, MarinBytes
    may process personal information on the basis of:
  </p>
  <ul>
    <li>
      Performance of a contract or taking steps at the request of the
      individual or customer before entering into a contract.
    </li>
    <li>Compliance with a legal or regulatory obligation.</li>
    <li>Consent, where consent is required.</li>
    <li>
      Legitimate interests, where recognised and permitted by applicable law.
    </li>
    <li>
      Protection of the security, rights, property, and integrity of MarinBytes,
      its customers, users, or systems.
    </li>
    <li>Other lawful grounds available under applicable data protection law.</li>
  </ul>
  <p>
    For processing governed by India's Digital Personal Data Protection
    framework, MarinBytes will apply the requirements applicable to the
    relevant processing and as the statutory provisions become effective.
    Where consent is the applicable basis, consent will be requested and
    managed in the manner required by law.
  </p>

  <h3>7. Customer Data, Roles, and Control</h3>
  <p>
    Customer-entered operational, vessel, crew, compliance, and business
    information remains under the control of the relevant customer, subject to
    the applicable customer agreement.
  </p>
  <p>
    Depending on the service configuration and applicable law, MarinBytes may
    act as a Data Fiduciary or equivalent controller for information it
    determines to process for its own purposes, and as a data processor/service
    provider or equivalent for information processed on behalf of a customer.
    The relevant role will be determined by the purpose, instructions,
    contractual arrangement, and applicable law.
  </p>
  <p>
    Where MarinBytes processes personal information on behalf of a customer,
    MarinBytes will use that information only as necessary to provide the
    contracted Services, maintain and secure the platform, provide support,
    perform authorised administration/configuration, comply with applicable
    law, and perform other activities expressly agreed with the customer.
  </p>

  <h3>8. Sharing and Disclosure</h3>
  <p>MarinBytes does not sell personal information.</p>
  <p>
    Information may be disclosed or made accessible where reasonably necessary
    for the purposes described in this Policy, including to:
  </p>
  <ul>
    <li>Customer administrators and authorised users.</li>
    <li>Hosting, cloud, storage, backup, and infrastructure providers.</li>
    <li>Authentication and identity providers.</li>
    <li>Email, communication, and notification service providers.</li>
    <li>Technical, cybersecurity, monitoring, and support service providers.</li>
    <li>Professional advisers and other service providers engaged by MarinBytes.</li>
    <li>
      Government authorities, regulators, courts, or law-enforcement bodies
      where required or permitted by applicable law.
    </li>
  </ul>
  <p>
    Service providers are expected to process information only to the extent
    necessary for their services and subject to appropriate contractual,
    confidentiality, security, and legal requirements.
  </p>

  <h3>9. International Processing and Transfers</h3>
  <p>
    Depending on the location of MarinBytes, its customers, users,
    infrastructure, and service providers, personal information may be
    processed or stored outside the country in which it was collected.
  </p>
  <p>
    Where applicable law imposes requirements or restrictions on cross-border
    transfers, MarinBytes will take steps appropriate to comply with those
    requirements, including any required contractual, organisational, or
    technical safeguards.
  </p>

  <h3>10. Data Retention and Deletion</h3>
  <p>
    MarinBytes retains personal information only for as long as reasonably
    necessary for the purposes described in this Policy, to provide the
    Services, fulfil contractual obligations, resolve disputes, maintain
    security, meet audit or regulatory requirements, and enforce applicable
    agreements.
  </p>
  <ul>
    <li>Retention periods may vary based on the type and sensitivity of information.</li>
    <li>
      Customer contractual requirements may determine retention periods for
      customer-controlled data.
    </li>
    <li>
      Legal, regulatory, accounting, maritime, security, and audit requirements
      may require longer retention.
    </li>
    <li>
      When information is no longer required, MarinBytes will take reasonable
      steps to delete, anonymise, or securely dispose of it, subject to legal
      and contractual requirements.
    </li>
  </ul>

  <h3>11. Data Security</h3>
  <p>
    MarinBytes implements reasonable technical and organisational measures
    designed to protect personal information against unauthorised access,
    alteration, disclosure, loss, misuse, or destruction.
  </p>
  <ul>
    <li>Access controls and role-based permissions.</li>
    <li>Authentication and account security mechanisms.</li>
    <li>Security and audit logging.</li>
    <li>System monitoring and operational safeguards.</li>
    <li>Backups and recovery measures.</li>
    <li>
      Appropriate technical and organisational safeguards based on the nature
      and risks of processing.
    </li>
  </ul>
  <p>
    No internet-based system or electronic storage method can be guaranteed to
    be completely secure. Users should also protect their devices, credentials,
    and access tokens and promptly report suspected unauthorised access.
  </p>

  <h3>12. User Responsibilities</h3>
  <ul>
    <li>Keep account credentials confidential and use only authorised accounts.</li>
    <li>Provide accurate and current information where required.</li>
    <li>Maintain reasonable security of devices used to access the Services.</li>
    <li>Do not share credentials with unauthorised persons.</li>
    <li>
      Use the Services only in accordance with applicable law, customer
      policies, and MarinBytes terms.
    </li>
    <li>
      Immediately notify MarinBytes of suspected unauthorised access or a
      security incident affecting the Services.
    </li>
  </ul>

  <h3>13. Cookies and Similar Technologies</h3>
  <p>
    MarinBytes may use cookies, session technologies, local storage, and
    similar technologies to enable essential functionality, maintain
    sessions, improve security, understand platform usage and performance,
    and improve the user experience.
  </p>
  <p>
    Where required by applicable law, MarinBytes will provide appropriate
    notice and obtain consent for non-essential cookies or similar
    technologies. Users may be able to control cookies through browser
    settings; disabling certain technologies may affect Service functionality.
  </p>

  <h3>14. Data Protection and Privacy Rights</h3>
  <p>
    Subject to applicable law, individuals may have rights relating to their
    personal information, which may include:
  </p>
  <ul>
    <li>Requesting access to or information about personal data processed about them.</li>
    <li>Requesting correction of inaccurate or incomplete personal data.</li>
    <li>Requesting deletion or erasure where legally available.</li>
    <li>Withdrawing consent where processing is based on consent.</li>
    <li>
      Requesting restriction or objecting to certain processing where the
      applicable law provides such rights.
    </li>
    <li>Requesting data portability where applicable.</li>
    <li>Raising a grievance or complaint regarding the processing of personal data.</li>
    <li>
      Making a complaint to the relevant data protection authority or statutory
      body where applicable.
    </li>
  </ul>
  <p>
    The availability, scope, procedure, and timing of these rights depend on
    the law applicable to the individual and the relevant processing. Rights
    may also be subject to lawful exemptions, contractual arrangements,
    security requirements, or other limitations.
  </p>
  <p>
    Requests may be submitted using the privacy contact details in Section 19.
    MarinBytes may need to verify the identity and authority of the requester
    before responding.
  </p>

  <h3>15. Children's Privacy</h3>
  <p>
    The Services are intended primarily for business and professional use and
    are not directed toward children. MarinBytes does not knowingly collect
    personal information from children where such collection is prohibited by
    applicable law.
  </p>

  <h3>16. Third-Party Services and Links</h3>
  <p>
    The Services may integrate with or contain links to third-party services.
    MarinBytes is not responsible for the privacy practices, content, security,
    or policies of third-party websites or services. Users should review the
    privacy policies of third-party services before providing personal
    information to them.
  </p>

  <h3>17. Data Breach and Security Incidents</h3>
  <p>
    If MarinBytes becomes aware of a security incident involving personal
    information, MarinBytes will assess the incident and take reasonable steps
    to contain, investigate, mitigate, remediate, and prevent recurrence as
    appropriate.
  </p>
  <p>
    Where notification is required by applicable law or contractual
    obligations, MarinBytes will provide notifications to the relevant
    customer, individuals, authorities, or other parties within the applicable
    timeframe and in the manner required by law.
  </p>

  <h3>18. Changes to This Privacy Policy</h3>
  <p>
    MarinBytes may update this Privacy Policy from time to time to reflect
    changes in the Services, technology, business practices, or legal and
    regulatory requirements.
  </p>
  <p>
    The updated version will be published through the applicable MarinBytes
    website, application, or other appropriate channel. Where required by
    applicable law, MarinBytes will provide additional notice or obtain
    consent for material changes.
  </p>

  <h3>19. Contact Us / Privacy Requests</h3>
  <p>
    For privacy-related questions, data protection requests, complaints, or
    concerns, users and customers may contact MarinBytes using the following
    details:
  </p>

  <p>
    <strong>Company:</strong> MarinBytes Pvt. Ltd.
  </p>
  <p>
    <strong>Privacy / Compliance Email:</strong>{" "}
    compliance@marinbytes.com
  </p>
  <p>
    <strong>Additional Contact Email:</strong>{" "}
    hiteshgupta@marinbytes.com
  </p>
  <p>
    <strong>Telephone and/or WhatsApp:</strong> +91 95094 33036
  </p>
  <p>
    <strong>Postal Address:</strong> 68/SP3, Sec 6, Pratap Nagar,
    Jaipur-33, Rajasthan, India
  </p>
  <p>
    MarinBytes will make reasonable efforts to respond to privacy-related
    requests and complaints within the timeframe required by applicable law,
    subject to identity verification, the nature of the request, and any
    lawful limitations.
  </p>

  <h3>20. Applicable Law and Jurisdiction</h3>
  <p>
    This Privacy Policy is intended to operate subject to applicable laws and
    regulations governing the relevant processing of personal information.
    For customers and users in India, this includes applicable Indian data
    protection and information technology laws and regulations. Where a
    customer or user is located in another jurisdiction, additional privacy
    rights and obligations may apply.
  </p>
  <p>
    Nothing in this Policy is intended to reduce or exclude any mandatory
    right or protection that cannot lawfully be excluded.
  </p>

  <p style={{ marginTop: "24px", fontSize: "12px", color: "#666" }}>
    <strong>Last Updated:</strong> 26 August 2026
  </p>

</div>
      </Modal>
      
    </div>
  );
};

export default Registration;
